import { useState, useEffect, useRef } from 'react';
import { Hospital, FilterType } from '../types/hospital.types';
import { extensiveSearchHospitals, getHospitalCounts } from '../../../Http/Index';
import { buildSearchPayload, buildCountsPayload } from '../utils/hospitalHelpers';

export const useHospitalData = (itemsPerPage: number = 10) => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchResultsCount, setSearchResultsCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [esicCount, setEsicCount] = useState(0);
  const [emergencyCount, setEmergencyCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isDeleted, setIsDeleted] = useState(false);
  const [insuranceFilter, setInsuranceFilter] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchHospitals = async (): Promise<void> => {
    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    try {
      const payload = buildSearchPayload(searchTerm, currentPage, filter, itemsPerPage, isDeleted, insuranceFilter);
      const response = await extensiveSearchHospitals(payload, abortControllerRef.current);
      setHospitals(response.data.data || []);
      setSearchResultsCount(response.data.total || 0);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        console.error('Error fetching hospitals:', error);
      }
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async (): Promise<void> => {
    try {
      const payload = buildCountsPayload();
      const response = await getHospitalCounts(payload);
      const counts = response.data.data;
      setTotalCount(counts.total || 0);
      setActiveCount(counts.active || 0);
      setEsicCount(counts.esic || 0);
      setEmergencyCount(counts.emergency || 0);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const updateLocalHospital = (hospitalId: string, updatedData: any): void => {
    setHospitals(prev => prev.map(h => 
      h._id === hospitalId ? { ...h, ...updatedData } : h
    ));
  };

  useEffect(() => {
    fetchHospitals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter, isDeleted]);

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchHospitals();
    }, 500);
    return () => {
      clearTimeout(timeoutId);
      // Abort any pending request when searchTerm or insuranceFilter changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, insuranceFilter]);

  return {
    hospitals,
    loading,
    totalCount,
    activeCount,
    esicCount,
    emergencyCount,
    searchResultsCount,
    searchTerm,
    filter,
    currentPage,
    isDeleted,
    insuranceFilter,
    setSearchTerm,
    setFilter,
    setCurrentPage,
    setIsDeleted,
    setInsuranceFilter,
    updateLocalHospital,
    fetchCounts,
    totalPages: Math.ceil(searchResultsCount / itemsPerPage)
  };
};